import { useEffect, useRef } from 'react'

import { debounce } from 'es-toolkit/function'
import { useCommonStore } from 'shared/stores/commonStore'
import { WindowState } from 'shared/types'

import { useWindowStore } from '../stores'

const calculateNewPosition = (
  window: WindowState,
  oldViewport: { width: number; height: number },
  newViewport: { width: number; height: number },
  topDockHeight: number
) => {
  const { position, size } = window

  // calculate available space in both viewports
  const oldAvailableWidth = Math.max(0, oldViewport.width - size.width)
  const oldAvailableHeight = Math.max(
    0,
    oldViewport.height - topDockHeight - size.height
  )
  const newAvailableWidth = Math.max(0, newViewport.width - size.width)
  const newAvailableHeight = Math.max(
    0,
    newViewport.height - topDockHeight - size.height
  )

  // if no space was available before, center the window
  if (oldAvailableWidth === 0 || oldAvailableHeight === 0) {
    return {
      x: Math.floor(Math.max(0, newAvailableWidth / 2)),
      y: Math.floor(
        Math.max(topDockHeight, topDockHeight + newAvailableHeight / 2)
      )
    }
  }

  // calculate relative position (0 to 1) with clamping
  const relativeX = Math.min(1, Math.max(0, position.x / oldAvailableWidth))
  const relativeY = Math.min(
    1,
    Math.max(0, (position.y - topDockHeight) / oldAvailableHeight)
  )

  // apply relative position to new viewport
  const newX = relativeX * newAvailableWidth
  const newY = relativeY * newAvailableHeight + topDockHeight

  // clamp to bounds
  return {
    x: Math.floor(Math.max(0, Math.min(newX, newAvailableWidth))),
    y: Math.floor(
      Math.max(
        topDockHeight,
        Math.min(newY, newAvailableHeight + topDockHeight)
      )
    )
  }
}

export const useSetWindowPositions = () => {
  const updateWindowPosition = useWindowStore(
    state => state.updateWindowPosition
  )
  const topDockHeight = useCommonStore(state => state.topDockHeight)

  const isRepositioningRef = useRef(false)
  const viewportRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight
  })

  useEffect(() => {
    const handleResize = () => {
      if (isRepositioningRef.current) return

      const oldViewport = viewportRef.current
      const newViewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }

      // skip if viewport size hasn't changed
      if (
        oldViewport.width === newViewport.width &&
        oldViewport.height === newViewport.height
      ) {
        return
      }

      isRepositioningRef.current = true

      // we don't get windows from the hook result because subscribing to it would cause the effect to re-run which we don't want
      const windows = useWindowStore.getState().windows

      windows.forEach(w => {
        if (w.isMaximized || w.snapArea) return

        const newPosition = calculateNewPosition(
          w,
          oldViewport,
          newViewport,
          topDockHeight
        )
        updateWindowPosition(w.id, newPosition)
      })

      viewportRef.current = newViewport

      setTimeout(() => {
        isRepositioningRef.current = false
      }, 200)
    }

    const debouncedResize = debounce(handleResize, 200)

    window.addEventListener('resize', debouncedResize)
    return () => {
      window.removeEventListener('resize', debouncedResize)
      debouncedResize.cancel()
    }
  }, [updateWindowPosition, topDockHeight])
}
